import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useCoordinatesHistory } from './CoOrdinateHistory.js';
import PoweredBy_Logo from "../../images/poweredBy.svg"
import config from "../../config.json";
import { useSettings } from "../../context/SettingsContext";
import i18n from "../../i18n";
import { formatDeviationDuration, getScheduleDeviation, getScheduleDeviationMessage } from "./scheduleDeviation";
import {
  normalizeDisplayLanguage,
  translateStopName,
} from "./vehicleScreenTranslations";
import {
  NEXT_STOP_HANDOFF_METERS,
  selectNextStopByRouteOrder,
} from "./nextStopSelection";

//constants
const CONSTANTS = {
  STOP_RADIUS: NEXT_STOP_HANDOFF_METERS, // meters - radius to detect if vehicle is at a stop
  STOP_VISIT_TIME: 0.01, // seconds - time needed at a stop to mark it as visited (increased from 2)
  DEPARTURE_DISTANCE: NEXT_STOP_HANDOFF_METERS, // meters - leave this far before the card moves to the next stop
  LOOK_AHEAD_DISTANCE: 1000, // meters - maximum distance to look ahead for future stops
  DATA_STALENESS_THRESHOLD: 30000,
  EARTH_RADIUS: 6371e3,
  MAX_STOP_DISTANCE: Infinity, // No limit - stops can be at any distance
  MIN_STOP_DISTANCE: 0, // Minimum distance between stops (0m)
  AVG_SPEED: 30, // Average speed in km/h
  MIN_STOP_TIME: 0.1, // Minimum time between stops in seconds
  MAX_STOP_TIME: 100, // Maximum time between stops in seconds
  TRAFFIC_MULTIPLIER: 1.1, // 10% buffer for traffic
  U_TURN_DETECTION_RADIUS: 40, // meters - radius to detect vehicle returning to visited stop
  U_TURN_CHECK_COUNT: 2, // number of recently visited stops to check for u-turn
  ARRIVAL_DISTANCE: NEXT_STOP_HANDOFF_METERS, // meters - distance to show "Arrived"
  ARRIVAL_TIME: 10, // seconds - time threshold to show "Arrived"
  METRO_PREPARE_DISTANCE: 1000, // meters - reminder announcement before station
};

const calculateRealisticETA = (distance) => {
  const distanceInKm = distance / 1000;
  let baseTime = (distanceInKm / CONSTANTS.AVG_SPEED) * 3600;
  baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;
  const numTrafficLights = Math.floor(distance / 800);
  const avgTrafficLightDelay = 20;
  baseTime += numTrafficLights * avgTrafficLightDelay;
  return Math.min(
    Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
    CONSTANTS.MAX_STOP_TIME
  );
};

const calculateImprovedDistance = (pos1, pos2) => {
  if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
    return Infinity;

  try {
    const φ1 = (pos1.lat * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = CONSTANTS.EARTH_RADIUS * c;

    return Math.min(
      Math.max(distance, CONSTANTS.MIN_STOP_DISTANCE),
      CONSTANTS.MAX_STOP_DISTANCE
    );
  } catch (error) {
    console.error("Error calculating distance:", error);
    return Infinity;
  }
};

const getUnitText = (unitKey, language, defaultValue) =>
  i18n.t(unitKey, {
    lng: language,
    defaultValue,
  });

const formatTime = (timeString, language = "en") => {
  if (!timeString) return "---";

  if (
    typeof timeString === "string" &&
    (timeString.includes("m") || timeString.includes("s"))
  ) {
    return timeString;
  }

  const seconds =
    typeof timeString === "string" ? parseInt(timeString) : timeString;
  if (isNaN(seconds) || seconds <= 0) return "---";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const hourUnit = getUnitText("hourShort", language, "h");
  const minuteUnit = getUnitText("minuteShort", language, "m");
  const secondUnit = getUnitText("secondShort", language, "s");

  if (hours > 0) return `${hours}${hourUnit} ${minutes}${minuteUnit}`;
  if (minutes > 0) return `${minutes}${minuteUnit} ${secs}${secondUnit}`;
  return `${secs}${secondUnit}`;
};

const NextStopTableScreenScreen = ({
  details,
  stops = [],
  stopDetails = {},
  isInbound,
  pathDirection,
  organizationId,
  currentPosition,
  routeCoordinates = [],
  onAnnouncementLanguageChange,
  tripStatus = null,
  scheduleTiming = null,
  adPlaying = false,
  onLiveTickerChange,
}) => {
  // console.log("📍 [NextStop] Component rendered with isInbound:", isInbound, "Position:", currentPosition);
  // State for visited stops tracking
  const { settings } = useSettings();
  const [visitedStops, setVisitedStops] = useState([]);
  const [stopVisitTimers, setStopVisitTimers] = useState({});
  const [currentStopId, setCurrentStopId] = useState(null);
  const [previousData, setPreviousData] = useState({
    nextStop: null,
    stopDetail: null,
    lastUpdateTime: null,
  });
  const [, setLastVisitedStop] = useState(null);
  const [announcementSettings, setAnnouncementSettings] = useState(null);
  const [activeAnnouncementLanguage, setActiveAnnouncementLanguage] = useState(null);
  const [translatedNextStopName, setTranslatedNextStopName] = useState("");
  const lastAnnouncementRef = useRef({ next: null, arrived: null, reminder: null, schedule: null });
  const reminderStateRef = useRef({ stopId: null, seenBeyondReminder: false });
  const announcementQueueRef = useRef(Promise.resolve());
  const activeAudioRef = useRef(null);
  const activeAudioUrlRef = useRef(null);
  const pendingPlayRetryRef = useRef(null);
  const isAnnouncementMountedRef = useRef(true);
  const adPlayingRef = useRef(false);
  const pendingAnnouncementRef = useRef(null);
  const finishActivePlaybackRef = useRef(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  adPlayingRef.current = Boolean(adPlaying);
  const lastConcretePathDirectionRef = useRef(null);
  const stopNameTranslationCacheRef = useRef(new Map());
  const { recentCoordinates, addCoordinate, clearHistory } = useCoordinatesHistory();
  const effectiveAnnouncementSettings = announcementSettings || settings || {};

  useEffect(() => {
    setAnnouncementSettings(null);
    if (!organizationId) return;

    let cancelled = false;

    const fetchAnnouncementSettings = async () => {
      try {
        const response = await fetch(
          `${config.baseURL}/appSettings/organization?organizationId=${organizationId}`,
          { credentials: "include" }
        );

        if (!response.ok) {
          throw new Error(`Settings API error: ${response.status}`);
        }

        const data = await response.json();
        if (!cancelled) {
          setAnnouncementSettings(data);
        }
      } catch (error) {
        console.error("Error fetching announcement settings:", error);
      }
    };

    fetchAnnouncementSettings();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const announcementLanguages = useMemo(() => {
    const fallbackLanguage = (
      effectiveAnnouncementSettings?.defaultLanguage ||
      localStorage.getItem("i18nextLng") ||
      "en"
    ).toLowerCase();
    const configuredLanguages = Array.isArray(
      effectiveAnnouncementSettings?.multilingualLanguages
    )
      ? effectiveAnnouncementSettings.multilingualLanguages
      : [];
    const languages =
      configuredLanguages.length > 0 ? configuredLanguages : [fallbackLanguage];

    return Array.from(
      new Set(
        languages
          .map((language) => String(language || "").trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }, [
    effectiveAnnouncementSettings?.defaultLanguage,
    effectiveAnnouncementSettings?.multilingualLanguages,
  ]);

  const languageSwitchIntervalMs = Math.max(
    0,
    Number(effectiveAnnouncementSettings?.languageSwitchInterval || 0)
  ) * 1000;
  const announcementLanguageKey = announcementLanguages.join(",");
  const activeDisplayLanguage = normalizeDisplayLanguage(
    activeAnnouncementLanguage ||
      announcementLanguages[0] ||
      effectiveAnnouncementSettings?.defaultLanguage ||
      (typeof window !== "undefined" ? localStorage.getItem("i18nextLng") : null) ||
      "en"
  );
  const translatedNextStopTitle = i18n.t("Next Stop", {
    lng: activeDisplayLanguage,
    defaultValue: "Next Stop",
  });
  const determiningNextStopText = i18n.t("Determining Next Stop", {
    lng: activeDisplayLanguage,
    defaultValue: "Determining Next Stop",
  });
  const etaLabel = i18n.t("ETA", {
    lng: activeDisplayLanguage,
    defaultValue: "ETA",
  });
  const distanceLabel = i18n.t("Distance", {
    lng: activeDisplayLanguage,
    defaultValue: "Distance",
  });
  const arrivedText = i18n.t("Arrived", {
    lng: activeDisplayLanguage,
    defaultValue: "Arrived",
  });
  const kilometerUnitText = i18n.t("km", {
    lng: activeDisplayLanguage,
    defaultValue: "km",
  });

  const waitBetweenAnnouncements = useCallback(
    () =>
      new Promise((resolve) => {
        if (!languageSwitchIntervalMs) {
          resolve();
          return;
        }

        setTimeout(resolve, languageSwitchIntervalMs);
      }),
    [languageSwitchIntervalMs]
  );

  const cleanupActiveAudio = useCallback(() => {
    pendingPlayRetryRef.current = null;
    const finishPlayback = finishActivePlaybackRef.current;
    finishActivePlaybackRef.current = null;
    const activeAudio = activeAudioRef.current;
    if (activeAudio) {
      activeAudio.onended = null;
      activeAudio.onerror = null;
      activeAudio.oncanplaythrough = null;
      activeAudio.pause();
      activeAudio.removeAttribute("src");
      activeAudio.load();
      activeAudioRef.current = null;
    }

    if (activeAudioUrlRef.current) {
      URL.revokeObjectURL(activeAudioUrlRef.current);
      activeAudioUrlRef.current = null;
    }
    if (finishPlayback) finishPlayback();
  }, []);

  const enableAnnouncementSound = useCallback(() => {
    setSoundBlocked(false);
    const retry = pendingPlayRetryRef.current;
    if (retry) {
      pendingPlayRetryRef.current = null;
      retry();
    }
  }, []);

  useEffect(() => {
    const onInteract = () => enableAnnouncementSound();
    window.addEventListener("pointerdown", onInteract);
    window.addEventListener("keydown", onInteract);
    return () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
  }, [enableAnnouncementSound]);

  const playAnnouncement = useCallback(
    ({ stopName, phase = "next", text, minutes } = {}) => {
      if (announcementLanguages.length === 0) return;
      if (
        !text &&
        !stopName &&
        phase !== "will_start" &&
        phase !== "delayed"
      ) {
        return;
      }

      const payload = { stopName, phase, text, minutes };
      if (adPlayingRef.current) {
        pendingAnnouncementRef.current = payload;
        return;
      }
      pendingAnnouncementRef.current = payload;

      announcementQueueRef.current = announcementQueueRef.current
        .catch(() => null)
        .then(async () => {
          if (!isAnnouncementMountedRef.current) return;
          if (adPlayingRef.current) return;

          for (let index = 0; index < announcementLanguages.length; index++) {
            const language = announcementLanguages[index];
            if (!isAnnouncementMountedRef.current || adPlayingRef.current) return;

            try {
              setActiveAnnouncementLanguage(language);
              onAnnouncementLanguageChange?.(language);

              const response = await fetch(`${config.baseURL}/announcement/tts`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(
                  text
                    ? { text, language }
                    : {
                        language,
                        phase,
                        ...(stopName ? { stop_name: stopName } : {}),
                        ...(minutes != null ? { minutes } : {}),
                      }
                ),
              });

              if (!response.ok) {
                const error = await response.json().catch(() => null);
                throw new Error(
                  error?.error || "Failed to generate announcement audio"
                );
              }

              const audioBlob = await response.blob();
              if (!isAnnouncementMountedRef.current || adPlayingRef.current) return;

              cleanupActiveAudio();

              const playableBlob =
                audioBlob.type === "audio/mpeg"
                  ? audioBlob
                  : new Blob([audioBlob], { type: "audio/mpeg" });
              const audioUrl = URL.createObjectURL(playableBlob);
              activeAudioUrlRef.current = audioUrl;

              await new Promise((resolve) => {
                const audio = new Audio(audioUrl);
                audio.preload = "auto";
                audio.volume = 1;
                audio.muted = false;
                activeAudioRef.current = audio;
                let settled = false;

                const finish = () => {
                  if (settled) return;
                  settled = true;
                  if (finishActivePlaybackRef.current === finish) {
                    finishActivePlaybackRef.current = null;
                  }
                  pendingPlayRetryRef.current = null;
                  if (activeAudioRef.current === audio) {
                    activeAudioRef.current = null;
                  }
                  if (activeAudioUrlRef.current === audioUrl) {
                    URL.revokeObjectURL(audioUrl);
                    activeAudioUrlRef.current = null;
                  }
                  resolve();
                };

                const startPlayback = () => {
                  audio
                    .play()
                    .then(() => setSoundBlocked(false))
                    .catch((error) => {
                      console.error("Error playing announcement:", error);
                      if (error?.name === "NotAllowedError") {
                        setSoundBlocked(true);
                        pendingPlayRetryRef.current = startPlayback;
                        return;
                      }
                      finish();
                    });
                };

                finishActivePlaybackRef.current = finish;
                audio.onended = finish;
                audio.onerror = () => {
                  console.error("Announcement audio element error", audio.error);
                  finish();
                };
                if (adPlayingRef.current) {
                  finish();
                  return;
                }
                startPlayback();
              });
            } catch (error) {
              console.error("Error playing announcement:", error);
            }

            if (adPlayingRef.current) return;
            if (index < announcementLanguages.length - 1) {
              await waitBetweenAnnouncements();
            }
          }

          if (pendingAnnouncementRef.current === payload) {
            pendingAnnouncementRef.current = null;
          }
        });
    },
    [
      announcementLanguages,
      cleanupActiveAudio,
      onAnnouncementLanguageChange,
      waitBetweenAnnouncements,
    ]
  );

  useEffect(() => {
    if (adPlaying) {
      cleanupActiveAudio();
      return;
    }
    const pending = pendingAnnouncementRef.current;
    if (!pending) return;
    pendingAnnouncementRef.current = null;
    playAnnouncement(pending);
  }, [adPlaying, cleanupActiveAudio, playAnnouncement]);

  const getBestMetrics = (
    calculatedDistance,
    backendDistance,
    calculatedETA,
    backendETA
  ) => {
    let finalDistance = calculatedDistance;
    let finalETA = calculatedETA;

    if (backendDistance) {
      const backendInMeters =
        backendDistance > 100 ? backendDistance : backendDistance * 1000;

      if (calculatedDistance && backendInMeters) {
        const diff = Math.abs(calculatedDistance - backendInMeters);
        if (diff / calculatedDistance < 0.5) {
          finalDistance = Math.min(calculatedDistance, backendInMeters);
        } else {
          finalDistance = calculatedDistance;
        }
      }
    }

    if (backendETA && calculatedETA) {
      const diff = Math.abs(calculatedETA - backendETA);
      if (diff / calculatedETA < 0.5) {
        finalETA = Math.min(calculatedETA, backendETA);
      } else {
        finalETA = calculatedETA;
      }
    }

    return {
      distance: finalDistance / 1000,
      eta: finalETA,
    };
  };

  const calculateDistance = (pos1, pos2) => {
    if (!pos1?.lat || !pos1?.lng || !pos2?.latitude || !pos2?.longitude)
      return Infinity;

    try {
      const φ1 = (pos1.lat * Math.PI) / 180;
      const φ2 = (parseFloat(pos2.latitude) * Math.PI) / 180;
      const Δφ = ((parseFloat(pos2.latitude) - pos1.lat) * Math.PI) / 180;
      const Δλ = ((parseFloat(pos2.longitude) - pos1.lng) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return CONSTANTS.EARTH_RADIUS * c;
    } catch (error) {
      console.error("Error calculating distance:", error);
      return Infinity;
    }
  };

  const findRouteIndex = (position) => {
    if (!position || !routeCoordinates?.length) return -1;

    let minDistance = Infinity;
    let closestIndex = -1;

    routeCoordinates.forEach((coord, index) => {
      const distance = calculateDistance(position, {
        latitude: coord.latitude,
        longitude: coord.longitude,
      });

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  // Log visited stops state for debugging
  // console.log("✅ [Visited Stops]:", visitedStops.map(v => v.name).join(", ") || "None");
  
  const resetTrackingState = useCallback(() => {
    setVisitedStops([]);
    setStopVisitTimers({});
    setCurrentStopId(null);
    setLastVisitedStop(null);
    setPreviousData({
      nextStop: null,
      stopDetail: null,
      lastUpdateTime: null,
    });
    lastAnnouncementRef.current = { next: null, arrived: null, reminder: null, schedule: null };
    reminderStateRef.current = { stopId: null, seenBeyondReminder: false };
    clearHistory();
  }, [clearHistory]);

  // Reset only when direction changes between concrete directions.
  useEffect(() => {
    if (!pathDirection) return;

    if (!lastConcretePathDirectionRef.current) {
      lastConcretePathDirectionRef.current = pathDirection;
      return;
    }

    if (lastConcretePathDirectionRef.current === pathDirection) return;

    lastConcretePathDirectionRef.current = pathDirection;
    resetTrackingState();
  }, [pathDirection, resetTrackingState]);

  // Determine next stop
  const { nextStop, stopDetail } = useMemo(() => {
    try {
      if (!currentPosition || !stops?.length || !routeCoordinates?.length) {
        return previousData;
      }

      const currentIndex = findRouteIndex(currentPosition);
      if (currentIndex === -1) return previousData;

      const stopsWithData = stops
        .map((stop) => ({
          ...stop,
          routeIndex: findRouteIndex({
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          }),
          distance: calculateImprovedDistance(currentPosition, stop),
        }))
        .filter((stop) => stop.routeIndex !== -1);

      const nextValidStop = selectNextStopByRouteOrder({
        stops: stopsWithData,
        currentIndex,
        isInbound,
        visitedStopIds: visitedStops.map((stop) => stop.stopId),
        handoffMeters: CONSTANTS.DEPARTURE_DISTANCE,
      });

      if (!nextValidStop) {
        return { nextStop: null, stopDetail: null };
      }
      // console.log("🎯 [Next Stop] Selected:", nextValidStop.name, "Distance:", (nextValidStop.distance / 1000).toFixed(2), "km");
      
      const backendStopDetail = stopDetails[nextValidStop.stopId];

      const calculatedDistance = nextValidStop.distance;
      const calculatedETA = calculateRealisticETA(calculatedDistance);

      const bestMetrics = getBestMetrics(
        calculatedDistance,
        backendStopDetail?.distance,
        calculatedETA,
        backendStopDetail?.eta
      );

      return {
        nextStop: nextValidStop,
        stopDetail: {
          ...backendStopDetail,
          distance: bestMetrics.distance,
          eta: bestMetrics.eta,
        },
      };
    } catch (error) {
      console.error("Error determining next stop:", error);
      return previousData;
    }
  }, [
    currentPosition,
    stops,
    stopDetails,
    routeCoordinates,
    isInbound,
    visitedStops,
  ]);

  useEffect(() => {
    const defaultLanguage = announcementLanguages[0] || null;
    setActiveAnnouncementLanguage(defaultLanguage);
    onAnnouncementLanguageChange?.(defaultLanguage);
  }, [
    announcementLanguages,
    announcementLanguageKey,
    nextStop?.stopId,
    onAnnouncementLanguageChange,
  ]);

  useEffect(() => {
    const stopName = nextStop?.name || "";

    if (!stopName) {
      setTranslatedNextStopName("");
      return;
    }

    if (activeDisplayLanguage === "en") {
      setTranslatedNextStopName(stopName);
      return;
    }

    let cancelled = false;
    setTranslatedNextStopName(stopName);

    const loadTranslatedStopName = async () => {
      try {
        const translatedText = await translateStopName(
          stopName,
          activeDisplayLanguage,
          stopNameTranslationCacheRef
        );

        if (!cancelled) {
          setTranslatedNextStopName(translatedText);
        }
      } catch (error) {
        console.error("Error translating stop name:", error);
        if (!cancelled) {
          setTranslatedNextStopName(stopName);
        }
      }
    };

    loadTranslatedStopName();

    return () => {
      cancelled = true;
    };
  }, [nextStop?.name, activeDisplayLanguage]);

  // Update previous data
  useEffect(() => {
    if (nextStop && stopDetail) {
      setPreviousData((prev) => {
        const isSameStop = prev.nextStop?.stopId === nextStop.stopId;
        const isSameDistance = prev.stopDetail?.distance === stopDetail.distance;
        const isSameEta = prev.stopDetail?.eta === stopDetail.eta;

        if (isSameStop && isSameDistance && isSameEta) {
          return prev;
        }

        return {
          nextStop,
          stopDetail,
          lastUpdateTime: Date.now(),
        };
      });
    }
  }, [nextStop, stopDetail]);

  // IMPROVED STOP VISIT TRACKING
  useEffect(() => {
    if (!currentPosition || !stops?.length) return;

    // Add coordinate to history
    const lastCoord = recentCoordinates[0];
    if (!lastCoord ||
      lastCoord.lat !== currentPosition.lat ||
      lastCoord.lng !== currentPosition.lng) {
      addCoordinate(currentPosition);
    }

    const visitedStopIds = new Set(visitedStops.map(v => v.stopId));

    stops.forEach((stop) => {
      const currentDistance = calculateDistance(currentPosition, stop);
      const isAtStop = currentDistance <= CONSTANTS.STOP_RADIUS;
      const isAlreadyVisited = visitedStopIds.has(stop.stopId);

      if (isAtStop && !isAlreadyVisited) {
        // Start or continue timing
        setStopVisitTimers(prev => {
          const existing = prev[stop.stopId];

          if (existing) {
            const timeSpent = (Date.now() - existing.startTime) / 1000;

            // Mark as visited if stayed long enough
            if (timeSpent >= CONSTANTS.STOP_VISIT_TIME) {
              // console.log(`✅✅✅ [VISIT CONFIRMED] Stop "${stop.name}" marked as VISITED (stayed ${timeSpent.toFixed(1)}s at ${currentDistance.toFixed(2)}m)`);
              
              setVisitedStops(current => {
                if (current.some((visited) => visited.stopId === stop.stopId)) {
                  return current;
                }

                const newVisited = [...current, {
                  stopId: stop.stopId,
                  timestamp: Date.now(),
                  name: stop.name
                }];
                // console.log(`📝 [Visited Stops Updated] Now: [${newVisited.map(v => v.name).join(", ")}]`);
                return newVisited;
              });
              setLastVisitedStop(stop);
              
              // Remove timer
              const { [stop.stopId]: _, ...rest } = prev;
              return rest;
            }
            // console.log(`⏱️ [Timer] Stop "${stop.name}" - ${timeSpent.toFixed(1)}s / ${CONSTANTS.STOP_VISIT_TIME}s (distance: ${currentDistance.toFixed(2)}m)`);
            return prev;
          }
          
          // Start new timer
          // console.log(`⏱️ [Timer Started] Stop "${stop.name}" at ${currentDistance.toFixed(2)}m`);
          return {
            ...prev,
            [stop.stopId]: { startTime: Date.now() }
          };
        });
      } else {
        // Remove timer if moved away or already visited
        setStopVisitTimers(prev => {
          if (prev[stop.stopId]) {
            const reason = isAlreadyVisited ? "already visited" : "moved away";
            // console.log(`❌ [Timer Cancelled] Stop "${stop.name}" (${reason}, distance: ${currentDistance.toFixed(2)}m)`);
          }
          const { [stop.stopId]: _, ...rest } = prev;
          return rest;
        });
      }
    });
  }, [
    currentPosition,
    stops,
    visitedStops,
    recentCoordinates,
    addCoordinate
  ]);

  // Determine if data is stale
  const isDataStale = useMemo(() => {
    if (!nextStop || !stopDetail) return true;
    if (!previousData.lastUpdateTime) return false;
    return (
      Date.now() - previousData.lastUpdateTime >
      CONSTANTS.DATA_STALENESS_THRESHOLD
    );
  }, [nextStop, stopDetail, previousData.lastUpdateTime]);

  // Check if vehicle is at the next stop
  const distanceToNextStop = nextStop && currentPosition
    ? calculateImprovedDistance(currentPosition, nextStop)
    : null;

  const calculatedETAValue = distanceToNextStop !== null 
    ? calculateRealisticETA(distanceToNextStop) 
    : null;

  // Show "Arrived" when distance < 100m AND time <= 10s
  const isArrivedConditionMet = distanceToNextStop !== null && 
    distanceToNextStop < CONSTANTS.ARRIVAL_DISTANCE && 
    calculatedETAValue !== null &&
    calculatedETAValue <= CONSTANTS.ARRIVAL_TIME;

  const isAtNextStop = distanceToNextStop !== null && distanceToNextStop <= CONSTANTS.STOP_RADIUS;
  const shouldPlayMetroReminder =
    nextStop?.stopId &&
    nextStop?.name &&
    distanceToNextStop !== null &&
    distanceToNextStop <= CONSTANTS.METRO_PREPARE_DISTANCE &&
    !isArrivedConditionMet &&
    reminderStateRef.current.stopId === nextStop.stopId &&
    reminderStateRef.current.seenBeyondReminder;

  const distance = distanceToNextStop !== null
    ? isArrivedConditionMet
      ? arrivedText
      : (distanceToNextStop / 1000).toFixed(1)
    : null;

  const eta = distanceToNextStop !== null
    ? isArrivedConditionMet
      ? arrivedText
      : formatTime(calculatedETAValue, activeDisplayLanguage)
    : null;

  useEffect(() => {
    onLiveTickerChange?.({
      name: translatedNextStopName || nextStop?.name || "",
      eta: eta || "",
      distance: distance
        ? distance === arrivedText
          ? arrivedText
          : `${distance} ${kilometerUnitText}`
        : "",
    });
  }, [
    onLiveTickerChange,
    translatedNextStopName,
    nextStop?.name,
    eta,
    distance,
    arrivedText,
    kilometerUnitText,
  ]);

 // AFTER
const scheduleDeviation = !isArrivedConditionMet && Number.isFinite(distanceToNextStop)
  ? getScheduleDeviation(
      scheduleTiming,
      nextStop?.id,
      calculatedETAValue,
      Date.now(),
      settings?.delayThresholdSeconds,
    )
  : null;
  const scheduleDeviationMessage = getScheduleDeviationMessage(scheduleDeviation);
  const scheduleDeviationValues = { ...scheduleDeviationMessage?.values };
  if (scheduleDeviation && scheduleDeviationValues.duration != null) {
    scheduleDeviationValues.duration = formatDeviationDuration(
      scheduleDeviation.minutes,
      (unitKey) => i18n.t(unitKey, { lng: activeDisplayLanguage, defaultValue: unitKey }),
    );
  }
  const scheduleDeviationText = scheduleDeviationMessage
    ? i18n.t(scheduleDeviationMessage.key, {
        lng: activeDisplayLanguage,
        ...scheduleDeviationValues,
        defaultValue: scheduleDeviationMessage.key,
      })
    : null;

 // TEMP DEBUG - remove after diagnosing
  console.log("🔍 SchedDebug", {
    scheduleTimingIsNull: scheduleTiming === null,
    scheduleTimingRaw: scheduleTiming,
    scheduledStopIds: scheduleTiming?.scheduledStops?.map(s => s.stopId),
    nextStopId: nextStop?.stopId,
    nextStopIdType: typeof nextStop?.stopId,
    calculatedETAValue,
    isArrivedConditionMet,
  });

  useEffect(() => {
    if (!nextStop?.stopId || distanceToNextStop === null) return;

    if (reminderStateRef.current.stopId !== nextStop.stopId) {
      reminderStateRef.current = {
        stopId: nextStop.stopId,
        seenBeyondReminder:
          distanceToNextStop > CONSTANTS.METRO_PREPARE_DISTANCE,
      };
      return;
    }

    if (distanceToNextStop > CONSTANTS.METRO_PREPARE_DISTANCE) {
      reminderStateRef.current = {
        ...reminderStateRef.current,
        seenBeyondReminder: true,
      };
    }
  }, [nextStop?.stopId, distanceToNextStop]);

  useEffect(() => {
    if (!nextStop?.stopId || !nextStop?.name || isArrivedConditionMet) return;

    const announcementKey = `${announcementLanguageKey}:${nextStop.stopId}`;
    if (lastAnnouncementRef.current.next === announcementKey) return;

    lastAnnouncementRef.current.next = announcementKey;
    playAnnouncement({
      stopName: nextStop.name,
      phase: "next",
    });
  }, [
    nextStop?.stopId,
    nextStop?.name,
    isArrivedConditionMet,
    announcementLanguageKey,
    playAnnouncement,
  ]);

  useEffect(() => {
    if (!shouldPlayMetroReminder) return;

    const announcementKey = `${announcementLanguageKey}:${nextStop.stopId}`;
    if (lastAnnouncementRef.current.reminder === announcementKey) return;

    lastAnnouncementRef.current.reminder = announcementKey;
    playAnnouncement({
      stopName: nextStop.name,
      phase: "next",
    });
  }, [
    shouldPlayMetroReminder,
    nextStop?.stopId,
    nextStop?.name,
    announcementLanguageKey,
    playAnnouncement,
  ]);

  useEffect(() => {
    if (!nextStop?.stopId || !nextStop?.name || !isArrivedConditionMet) return;

    const announcementKey = `${announcementLanguageKey}:${nextStop.stopId}`;
    if (lastAnnouncementRef.current.arrived === announcementKey) return;

    lastAnnouncementRef.current.arrived = announcementKey;
    playAnnouncement({
      stopName: nextStop.name,
      phase: "arrived",
    });
  }, [
    nextStop?.stopId,
    nextStop?.name,
    isArrivedConditionMet,
    announcementLanguageKey,
    playAnnouncement,
  ]);

  useEffect(() => {
    if (!tripStatus?.message || !tripStatus?.eventType) return;

    const isLeadEvent =
      tripStatus.eventType === "WILL_START" ||
      tripStatus.eventType === "WILL_REACH_STOP";
    const stopId = tripStatus.stopId ?? "none";
    const occurrenceKey = tripStatus.occurrenceKey ?? tripStatus.scheduleId ?? "none";
    const announcementKey = isLeadEvent
      ? `${announcementLanguageKey}:${tripStatus.eventType}:${stopId}:${occurrenceKey}`
      : `${announcementLanguageKey}:${tripStatus.eventType}:${stopId}:${occurrenceKey}:${tripStatus.minutes}`;

    if (lastAnnouncementRef.current.schedule === announcementKey) return;

    lastAnnouncementRef.current.schedule = announcementKey;
    const stitchPhase =
      tripStatus.eventType === "WILL_REACH_STOP"
        ? "will_reach"
        : tripStatus.eventType === "WILL_START"
          ? "will_start"
          : tripStatus.eventType === "VEHICLE_DELAYED"
            ? "delayed"
            : tripStatus.eventType === "LATE_TO_STOP"
              ? "late_to_stop"
              : null;
    const includeStop =
      stitchPhase === "will_reach" || stitchPhase === "late_to_stop";
    if (stitchPhase) {
      playAnnouncement({
        ...(includeStop && tripStatus.stopName
          ? { stopName: tripStatus.stopName }
          : {}),
        phase: stitchPhase,
        minutes: tripStatus.minutes,
      });
    }
  }, [
    tripStatus?.message,
    tripStatus?.eventType,
    tripStatus?.stopId,
    tripStatus?.minutes,
    tripStatus?.occurrenceKey,
    tripStatus?.scheduleId,
    announcementLanguageKey,
    playAnnouncement,
  ]);

  useEffect(() => {
    return () => {
      isAnnouncementMountedRef.current = false;
      cleanupActiveAudio();
    };
  }, [cleanupActiveAudio]);

  // Mark as visited when arrival conditions are met
  useEffect(() => {
    if (isArrivedConditionMet && nextStop && !visitedStops.find(v => v.stopId === nextStop.stopId)) {
      // console.log(`✅ Stop ${nextStop.name} marked as VISITED (distance: ${distanceToNextStop.toFixed(2)}m, ETA: ${calculatedETAValue}s)`);
      
      setVisitedStops(current => {
        if (current.some((visited) => visited.stopId === nextStop.stopId)) {
          return current;
        }

        return [...current, {
          stopId: nextStop.stopId,
          timestamp: Date.now(),
          name: nextStop.name
        }];
      });
      setLastVisitedStop(nextStop);
    }
  }, [isArrivedConditionMet, nextStop, distanceToNextStop, calculatedETAValue, visitedStops]);

  return (
    <Box
      sx={{
        width: { xs: '100vw', sm: '90vw', md: '50vw', lg: '42vw' },
        minHeight: { xs: '80vh', sm: 'calc(97.6vh - var(--ad-ticker-space, 0px))' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#E8EAEE',
        color: '#F9E719',
        textAlign: 'center',
        borderRadius: "20px",
        boxSizing: 'border-box',
        margin: "12px 16px 12px 0px"
      }}
    >
      <Box
        sx={{
          borderRadius: "10px 10px 0px 0px",
          width: '100%',
          zIndex: 1000,
          backgroundColor: '#183059',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '10px 8px',
        }}
      >
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <svg width="42" height="42" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3.16699 2.66699H11.167V13.3337H8.50033V29.3337H5.83366V13.3337H3.16699V2.66699ZM13.8337 26.3151V28.0003C13.8337 28.7367 14.4306 29.3337 15.167 29.3337C15.9034 29.3337 16.5003 28.7367 16.5003 28.0003V27.3337H25.8337V28.0003C25.8337 28.7367 26.4306 29.3337 27.167 29.3337C27.9034 29.3337 28.5003 28.7367 28.5003 28.0003V26.3151C29.3186 25.5827 29.8337 24.5183 29.8337 23.3337V12.3151C29.8337 10.821 28.9643 9.30236 27.3289 8.8242C25.977 8.42898 23.8842 8.00033 21.167 8.00033C18.4498 8.00033 16.357 8.42898 15.0051 8.8242C13.3697 9.30236 12.5003 10.821 12.5003 12.3151V23.3337C12.5003 24.5183 13.0153 25.5827 13.8337 26.3151ZM27.167 14.3337V17.667H15.167V14.3337H27.167ZM17.8337 23.3337C18.5701 23.3337 19.167 22.7367 19.167 22.0003C19.167 21.2639 18.5701 20.667 17.8337 20.667C17.0973 20.667 16.5003 21.2639 16.5003 22.0003C16.5003 22.7367 17.0973 23.3337 17.8337 23.3337ZM25.8337 22.0003C25.8337 22.7367 25.2367 23.3337 24.5003 23.3337C23.7639 23.3337 23.167 22.7367 23.167 22.0003C23.167 21.2639 23.7639 20.667 24.5003 20.667C25.2367 20.667 25.8337 21.2639 25.8337 22.0003Z" fill="white" />
          </svg>

          <span
            style={{
              color: '#FFFFFF',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 500,
              fontStyle: 'normal',
              fontSize: '46px',
              lineHeight: '100%',
              letterSpacing: '0%',
              whiteSpace: 'nowrap',
            }}
          >
            {translatedNextStopTitle}
          </span>
        </div>
      </Box>
      {soundBlocked ? (
        <Box
          onClick={enableAnnouncementSound}
          sx={{
            width: "100%",
            backgroundColor: "#F9E719",
            color: "#183059",
            padding: "10px 16px",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 700,
            fontSize: { xs: "0.95rem", sm: "1.1rem" },
            cursor: "pointer",
          }}
        >
          Tap to enable announcements
        </Box>
      ) : null}
      {tripStatus?.message ? (
        <Box
          sx={{
            width: "100%",
            backgroundColor:
              tripStatus.eventType === "LATE_TO_STOP" ||
              tripStatus.eventType === "VEHICLE_DELAYED"
                ? "#7A1F1F"
                : "#1F4E79",
            color: "#F9E719",
            padding: "8px 16px",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            fontSize: { xs: "0.95rem", sm: "1.15rem" },
          }}
        >
          {tripStatus.message}
        </Box>
      ) : null}

      <Box sx={{
        borderRadius: '16px',
        padding: { xs: '0.5rem', sm: '0.1rem' },
        width: { xs: '90%', sm: '95%' },
        marginBottom: { xs: '0.5rem', sm: '0.75rem' },
        height: "auto",
        minHeight: "fit-content",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-around',
          width: { xs: '90%', sm: '99%' },
          gap: { xs: '0.5rem', sm: '1rem' },
          flexDirection: { xs: 'column', sm: 'column', md: 'column' },
          margin: "auto"
        }}>
          <Box sx={{
            backgroundColor: '#183059',
            borderRadius: '16px',
            padding: { xs: '0.5rem', sm: '1rem' },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h2"
              sx={{
                color: '#F9E719',
                fontWeight: 'bold',
                fontSize: {
                  xs: '1.5rem',
                  sm: '2rem',
                  md: '4rem',
                },
              }}>
              {translatedNextStopName || nextStop?.name || determiningNextStopText}
            </Typography>
          </Box>

          <Box sx={{
            backgroundColor: '#183059',
            borderRadius: '16px',
            padding: { xs: '0.5rem', sm: '1rem' },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h3"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                marginBottom: { xs: '0.25rem', sm: '0.5rem' },
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '1.5rem',
                },
              }}>
              {etaLabel}
            </Typography>
            <Typography variant="h2"
              sx={{
                color: '#F9E719',
                fontWeight: 'bold',
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '3rem',
                },
              }}>
              {eta || <CircularProgress color="inherit" />}
            </Typography>
            {scheduleDeviationText && (
              <Typography sx={{ color: 'white', mt: 1, textAlign: 'center', fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}>
                {scheduleDeviationText}
              </Typography>
            )}
          </Box>

          <Box sx={{
            backgroundColor: '#183059',
            borderRadius: '16px',
            padding: { xs: '0.5rem', sm: '1rem' },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h3"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                marginBottom: { xs: '0.25rem', sm: '0.5rem' },
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '1.5rem',
                },
              }}>
              {distanceLabel}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: '#F9E719',
                fontWeight: 'bold',
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '3rem',
                },
              }}
            >
              {distance ? (
                distance === arrivedText ? (
                  arrivedText
                ) : (
                  `${distance} ${kilometerUnitText}`
                )
              ) : (
                <CircularProgress color="inherit" />
              )}
            </Typography>

          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          fontFamily: 'Roboto',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: '16px',
          lineHeight: '100%',
          color: '#000000',
          padding: "16px",
          alignItems: "center",
          display: "flex",
          gap: "10px"
        }}
      >
        Powered by
        <img src={PoweredBy_Logo} alt="Powered by logo" />
      </Box>
    </Box>
  );
};

export default NextStopTableScreenScreen;
